import { useMemo } from "react"
import { ModalBody, ModalFooter, useStyletron } from "@mezo-org/mezo-clay"
import { Form, FormikProps, withFormik } from "formik"

import {
  RootState,
  selectTokenBalance,
  selectTokenDecimals,
  useDappSelector,
} from "#/redux-state"
import { FieldAssetInput } from "#/shared/components/forms"
import SummaryList from "#/shared/components/SummaryList"
import { vault } from "#/shared/constants"
import { useEstimateReceiptTokenAmount } from "#/shared/hooks"
import { VaultDepositFormValue, VaultTokens, vaultTokens } from "#/shared/types"
import {
  bigIntToFloat,
  bigIntToHumanReadableFormat,
  formatDuration,
  vaultUtils,
} from "#/shared/utils"
import { StepperModalButton } from "#/ui/Modal/StepperModal"

type VaultDepositFormProps = {
  initialValues: VaultDepositFormValue
  onSubmitForm: (values: VaultDepositFormValue) => void
}

function VaultDepositFormBase({
  handleSubmit,
  getFieldMeta,
  isValid,
}: FormikProps<VaultDepositFormValue>) {
  const [css, theme] = useStyletron()

  const { value: amount } = getFieldMeta<bigint | undefined>("amount")
  const { value: token } = getFieldMeta<VaultTokens>("token")

  const tokenBalance = useDappSelector((state: RootState) =>
    selectTokenBalance(state, token),
  )

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token),
  )

  const receiptTokenAmount = useEstimateReceiptTokenAmount(token, amount)

  const summary = useMemo(
    () => [
      {
        label: "You’ll receive",
        endEnhancer: `${bigIntToHumanReadableFormat(receiptTokenAmount)} ${
          vault.VAULT_DEPOSIT_TOKEN_TO_SHARE_TOKEN_NAME[token]
        }`,
      },
      {
        label: "Collateral exposure",
        endEnhancer: "ETH",
      },
      {
        label: "Avg APY",
        endEnhancer: `${vaultUtils.getAvgAPY()}%`,
      },
      {
        label: "Withdrawal cooldown",
        endEnhancer: formatDuration(vault.WITHDRAWAL_COOLDOWN, "h'h"),
      },
    ],
    [receiptTokenAmount, token],
  )

  return (
    <Form
      onSubmit={handleSubmit}
      className={css({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      })}
    >
      <ModalBody
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          marginBottom: theme.sizing.scale500,
        }}
      >
        <FieldAssetInput
          withMaxButton
          maxTokenAmount={bigIntToFloat(
            tokenBalance,
            tokenDecimals,
            tokenDecimals,
          )}
          tokens={[...vaultTokens]}
        />
        <SummaryList list={summary} />
      </ModalBody>
      <ModalFooter>
        <StepperModalButton
          type="submit"
          disabled={!isValid || amount === undefined}
        >
          Review
        </StepperModalButton>
      </ModalFooter>
    </Form>
  )
}

const VaultDepositForm = withFormik<
  VaultDepositFormProps,
  VaultDepositFormValue
>({
  mapPropsToValues: ({ initialValues }) => initialValues,
  handleSubmit: (values, { props }) => props.onSubmitForm(values),
  // Validation is done at the asset input level
  validateOnBlur: false,
  validateOnChange: false,
  validateOnMount: false,
})(VaultDepositFormBase)

export default VaultDepositForm
